import { enqueue, removeOutbox, TRACKING_PRIORITY, type OutboxItem } from "../outbox";
import { phaseElapsedMs } from "../phaseClock";

export type QueuedEvent = {
  id: string;
  seq: number;
  type: string;
  phase_key: string | null;
  client_time: string;
  phase_elapsed_ms: number | null;
  payload: Record<string, unknown>;
};

export type TrackingStatus = number | "network";
export type TrackingClass = "ok" | "retry" | "drop" | "split";

const MAX_EVENTS = 100;
const MAX_CHUNK_BYTES = 256 * 1024;
const KEEPALIVE_BYTES = 60 * 1024;

let buffer: QueuedEvent[] = [];
let flushing = false;
let hideBusy = false;
let clock: { entered: number | null; serverNow: number; syncedAt: number } | null = null;
const beforeHide: Array<() => void> = [];

export function notePhaseClock(
  phaseEnteredAtMs: number | null,
  serverNowMs: number,
  syncedAtMs: number,
): void {
  clock = { entered: phaseEnteredAtMs, serverNow: serverNowMs, syncedAt: syncedAtMs };
}

export function currentPhaseElapsed(now = Date.now()): number | null {
  if (!clock) return null;
  return phaseElapsedMs(clock.entered, clock.serverNow, clock.syncedAt, now);
}

export function addBeforeHide(fn: () => void): () => void {
  beforeHide.push(fn);
  return () => {
    const index = beforeHide.indexOf(fn);
    if (index >= 0) beforeHide.splice(index, 1);
  };
}

export function classifyTracking(status: TrackingStatus): TrackingClass {
  if (status === "network") return "retry";
  if (status >= 200 && status < 300) return "ok";
  if (status === 400) return "drop";
  if (status === 413) return "split";
  return "retry";
}

/** A non-2xx answer, including decision_incomplete (400), stays in the outbox. */
export function keepsAnswerOnStatus(status: number): boolean {
  return status < 200 || status >= 300;
}

export function chunkEvents(
  events: QueuedEvent[],
  maxEvents = MAX_EVENTS,
  maxBytes = MAX_CHUNK_BYTES,
): QueuedEvent[][] {
  const chunks: QueuedEvent[][] = [];
  let current: QueuedEvent[] = [];
  for (const event of events) {
    if (current.length > 0 && (current.length >= maxEvents || byteLength(current, event) > maxBytes)) {
      chunks.push(current);
      current = [];
    }
    current.push(event);
  }
  if (current.length) chunks.push(current);
  return chunks;
}

function byteLength(events: QueuedEvent[], extra?: QueuedEvent): number {
  const body = extra ? [...events, extra] : events;
  return new TextEncoder().encode(JSON.stringify({ events: body })).length;
}

export function trackingOutboxItem(id: string, events: QueuedEvent[]): OutboxItem {
  return {
    id,
    priority: TRACKING_PRIORITY,
    path: "/api/participant/events",
    method: "POST",
    body: { events },
    created_at: Date.now(),
  };
}

/** Sends one chunk, splitting a 413. Returns the events that still need a later retry. */
export async function deliverEvents(
  events: QueuedEvent[],
  post: (events: QueuedEvent[]) => Promise<{ status: TrackingStatus }>,
  log: (message: string) => void = () => {},
): Promise<QueuedEvent[]> {
  if (events.length === 0) return [];
  let posted: { status: TrackingStatus };
  try {
    posted = await post(events);
  } catch {
    posted = { status: "network" };
  }
  const kind = classifyTracking(posted.status);
  if (kind === "ok") return [];
  if (kind === "drop") {
    log(`dropped ${events.length} tracking events (${posted.status})`);
    return [];
  }
  if (kind === "split") {
    if (events.length <= 1) {
      log("dropped oversized tracking event");
      return [];
    }
    const mid = Math.ceil(events.length / 2);
    const left = await deliverEvents(events.slice(0, mid), post, log);
    if (left.length) return [...left, ...events.slice(mid)];
    return deliverEvents(events.slice(mid), post, log);
  }
  return events;
}

type TokenFn = () => string | null;
type SeqFn = (token: string | null) => number;
let tokenFn: TokenFn = () => null;
let seqFn: SeqFn = () => 1;
let retryStoredTracking: () => void = () => {};

export function bindTrackingRetry(retry: () => void): void {
  retryStoredTracking = retry;
}

export function bindEventSource(seq: SeqFn, token: TokenFn): void {
  seqFn = seq;
  tokenFn = token;
}

export async function queueEvent(
  type: string,
  payload: Record<string, unknown>,
  phase_key?: string | null,
): Promise<void> {
  try {
    buffer.push({
      id: crypto.randomUUID(),
      seq: seqFn(tokenFn()),
      type,
      phase_key: phase_key ?? null,
      client_time: new Date().toISOString(),
      phase_elapsed_ms: currentPhaseElapsed(),
      payload,
    });
  } catch (error) {
    console.warn("queueEvent failed", error);
  }
}

export function pendingEvents(): QueuedEvent[] {
  return buffer;
}

export function scheduleFlush(): void {
  queueMicrotask(() => {
    void flushBuffer();
  });
}

async function persistAndSend(chunks: QueuedEvent[][], keepalive: boolean): Promise<void> {
  for (const chunk of chunks) {
    const item = trackingOutboxItem(crypto.randomUUID(), chunk);
    await enqueue(item);
    const retry = await deliverEvents(chunk, (events) => postEvents(events, keepalive));
    if (retry.length === chunk.length) continue;
    await removeOutbox(item.id);
    if (retry.length) await enqueue(trackingOutboxItem(crypto.randomUUID(), retry));
  }
}

export async function flushBuffer(): Promise<void> {
  if (flushing || buffer.length === 0) return;
  flushing = true;
  try {
    const taken = buffer.splice(0, buffer.length);
    await persistAndSend(chunkEvents(taken, MAX_EVENTS, MAX_CHUNK_BYTES), false);
  } finally {
    flushing = false;
  }
}

export async function flushOnHide(): Promise<void> {
  if (hideBusy) return;
  hideBusy = true;
  try {
    for (const fn of [...beforeHide]) {
      try {
        fn();
      } catch {
        /* a tracker failure must not block the save */
      }
    }
    if (buffer.length === 0) return;
    const taken = buffer.splice(0, buffer.length);
    await persistAndSend(chunkEvents(taken, MAX_EVENTS, KEEPALIVE_BYTES), true);
  } finally {
    hideBusy = false;
  }
}

/** Five-second tick: save and send new events, then retry tracking rows already stored. */
export async function onTrackingTimer(): Promise<void> {
  try {
    await flushBuffer();
  } catch {
    /* a tracking failure must not throw into the page */
  }
  try {
    retryStoredTracking();
  } catch {
    /* tracking */
  }
}

async function postEvents(
  events: QueuedEvent[],
  keepalive: boolean,
): Promise<{ status: TrackingStatus }> {
  try {
    const headers = new Headers({ "content-type": "application/json" });
    const token = tokenFn();
    if (token) headers.set("x-participant-token", token);
    const res = await fetch("/api/participant/events", {
      method: "POST",
      headers,
      body: JSON.stringify({ events }),
      credentials: "include",
      keepalive,
    });
    return { status: res.status };
  } catch {
    return { status: "network" };
  }
}

export function resetBufferForTests(): void {
  buffer = [];
  flushing = false;
  hideBusy = false;
  clock = null;
  beforeHide.length = 0;
}

function startBrowserFlush(): void {
  if (typeof window === "undefined") return;
  window.setInterval(() => {
    void onTrackingTimer();
  }, 5000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushOnHide();
  });
  window.addEventListener("pagehide", () => {
    void flushOnHide();
  });
}

startBrowserFlush();
